import React, { useState, useRef, useEffect } from 'react';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  StyleSheet,
  ActivityIndicator,
  Keyboard,
  Image,
  Dimensions,
  StatusBar,
  KeyboardAvoidingView,
  Platform,
  ScrollView,
  Switch,
} from 'react-native';
import { NavigationProp, RouteProp } from '@react-navigation/native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { routeToPendingInvitationIfAny } from '../../navigation/pendingInvitation';
import DeviceInfo from 'react-native-device-info';
import { API_URL } from '../../config';

import { createLogger } from '../../utils/logger';
import { ms, s, vs } from '../../utils/responsive';
import { analytics, AnalyticsEvent } from '../../services/analytics';
import { pickPostAuthTarget, wizardScreenForStep, letterScreenForStep } from '../../navigation/postAuthRoute';
import { shouldRouteToUnlockAtSignIn } from '../../navigation/postAuthUnlockRoute';
import {
  setAuthToken,
  setDeviceTrustToken,
  setRefreshToken,
} from '../../services/auth/tokenStorage';
import {
  checkRateLimit,
  RateLimitStatus,
  recordFailure,
  recordSuccess,
} from '../../services/auth/rateLimiter';
import RateLimitBanner from '../../components/RateLimitBanner';
const logger = createLogger('TotpVerification');

const { width, height } = Dimensions.get('window');

interface TotpVerificationScreenProps {
  navigation: NavigationProp<any>;
  route: RouteProp<{ params: { userId: string; email: string; secondFactorTicket?: string } }, 'params'>;
}

const TotpVerificationScreen: React.FC<TotpVerificationScreenProps> = ({
  navigation,
  route,
}) => {
  const { userId, email, secondFactorTicket } = route.params;
  const [code, setCode] = useState(['', '', '', '', '', '']);
  const [isVerifying, setIsVerifying] = useState(false);
  const [useBackupCode, setUseBackupCode] = useState(false);
  const [backupCode, setBackupCode] = useState('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [rememberDevice, setRememberDevice] = useState(false);
  const [rateLimit, setRateLimit] = useState<RateLimitStatus | null>(null);

  const inputRefs = useRef<Array<TextInput | null>>([]);

  useEffect(() => {
    analytics.track(AnalyticsEvent.TotpVerificationShown);
    // Surface any leftover lockout from a previous session immediately.
    checkRateLimit('totp').then((status) => {
      if (!status.allowed) setRateLimit(status);
    }).catch(() => { /* best-effort */ });
  }, []);

  const handleCodeChange = (text: string, index: number) => {
    // Clear error when user starts typing
    if (errorMessage) setErrorMessage(null);

    if (text && !/^\d$/.test(text)) {
      return;
    }

    const newCode = [...code];
    newCode[index] = text;
    setCode(newCode);

    if (text && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }

    if (index === 5 && text) {
      const fullCode = [...newCode.slice(0, 5), text].join('');
      handleVerify(fullCode);
    }
  };

  const handleKeyPress = (e: any, index: number) => {
    if (e.nativeEvent.key === 'Backspace' && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const getDeviceInfo = async () => {
    try {
      const deviceName = await DeviceInfo.getDeviceName();
      const devicePlatform = Platform.OS;
      const deviceId = await DeviceInfo.getUniqueId();

      return {
        deviceName: deviceName || `${Platform.OS} device`,
        devicePlatform,
        deviceFingerprint: deviceId,
      };
    } catch (error) {
      logger.debug('Error getting device info:', error);
      return {
        deviceName: `${Platform.OS} device`,
        devicePlatform: Platform.OS,
        deviceFingerprint: undefined,
      };
    }
  };

  const handleVerify = async (verificationCode?: string) => {
    const codeToVerify = useBackupCode ? backupCode : (verificationCode || code.join(''));

    logger.debug('TotpVerificationScreen - handleVerify called:', {
      userId,
      codeLength: codeToVerify.length,
      useBackupCode,
      rememberDevice,
      apiUrl: API_URL,
    });

    if (!useBackupCode && codeToVerify.length !== 6) {
      logger.debug('Code validation failed: not 6 digits');
      setErrorMessage('Please enter all 6 digits');
      return;
    }

    if (useBackupCode && codeToVerify.length !== 8) {
      logger.debug('Backup code validation failed: not 8 characters');
      setErrorMessage('Backup codes are 8 characters long');
      return;
    }

    // Pre-flight rate-limit check. The TOTP counter is distinct from
    // login's — a user who typo'd TOTP codes shouldn't be locked out of
    // password entry (they'd never be able to recover).
    const preCheck = await checkRateLimit('totp');
    if (!preCheck.allowed) {
      setRateLimit(preCheck);
      return;
    }

    setIsVerifying(true);
    setErrorMessage(null);
    Keyboard.dismiss();

    try {
      const url = `${API_URL}/auth/verify-totp`;
      logger.debug('Making TOTP verification request to:', url);

      // Get device info if remember device is checked
      const deviceInfo = rememberDevice ? await getDeviceInfo() : undefined;

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userId,
          token: codeToVerify,
          // The password step's ticket: the server gives a session only to a
          // code that follows the right password (2026-09-23).
          secondFactorTicket,
          rememberDevice,
          deviceInfo,
        }),
      });

      logger.debug('Response status:', response.status, response.statusText);

      const data = await response.json();
      logger.debug('Response data:', data);

      if (response.ok) {
        logger.debug('TOTP verification successful');
        analytics.track(AnalyticsEvent.TotpVerificationSucceeded, {
          used_backup_code: useBackupCode,
          remember_device: rememberDevice,
        });
        if (data.user?.id) analytics.identify(String(data.user.id));
        // Correct TOTP (or backup code) — drop the counter.
        try {
          await recordSuccess('totp');
          setRateLimit(null);
        } catch (rlErr) {
          logger.error('recordSuccess(totp) failed:', rlErr);
        }
        // Store auth tokens (access + refresh in keychain)
        await setAuthToken(data.access_token);
        if (data.refresh_token) {
          await setRefreshToken(data.refresh_token);
        }
        await AsyncStorage.setItem('@user_id', data.user.id);
        await AsyncStorage.setItem('@user_email', data.user.email);

        // Store account setup flags for session persistence
        // These ensure user is taken to Login (not SignUp) when token expires
        await AsyncStorage.setItem('@email_verified', 'true');
        await AsyncStorage.setItem('@2fa_setup_complete', 'true');
        await AsyncStorage.setItem('hasLoggedIn', 'true');

        // Store device trust token (30-day) in the keychain if provided
        if (data.deviceTrustToken) {
          await setDeviceTrustToken(data.deviceTrustToken);
          logger.debug('Device trust token stored for 30-day remember me');
        }

        // An invitation opened before sign-in comes first.
        if (await routeToPendingInvitationIfAny(navigation)) return;

        // Navigate based on onboarding status (new wizard flow takes precedence)
        const target = pickPostAuthTarget(data.user, {
          legacyOnboardingCompleted: !!data.isOnboardingCompleted,
        });
        if (target.kind === 'letterFlow') {
          navigation.reset({
            index: 0,
            routes: [{
              name: 'LetterFirst',
              state: { routes: [{ name: letterScreenForStep(target.initialStep) }] },
            }],
          });
        } else if (target.kind === 'wizard') {
          navigation.reset({
            index: 0,
            routes: [{
              name: 'OnboardingWizard',
              state: { routes: [{ name: wizardScreenForStep(target.initialStep) }] },
            }],
          });
        } else if (target.kind === 'dashboard') {
          // Gate: if the user has enrolled a recovery code but the
          // unwrapped key isn't cached on this device, route to
          // UnlockVault before Dashboard.
          if (await shouldRouteToUnlockAtSignIn()) {
            navigation.reset({
              index: 0,
              routes: [{ name: 'Auth', state: { routes: [{ name: 'UnlockVault' }] } }],
            });
          } else {
            navigation.reset({
              index: 0,
              routes: [{ name: 'Dashboard' }],
            });
          }
        } else {
          navigation.reset({
            index: 0,
            routes: [{ name: 'Splash' }],
          });
        }
      } else {
        logger.debug('TOTP verification failed:', data);
        analytics.track(AnalyticsEvent.TotpVerificationFailed, { used_backup_code: useBackupCode });
        setErrorMessage(
          useBackupCode
            ? 'The backup code is invalid or has already been used.'
            : "The verification code you entered doesn't match please re-check and try again or use a backup code."
        );
        if (useBackupCode) {
          setBackupCode('');
        } else {
          setCode(['', '', '', '', '', '']);
          setTimeout(() => inputRefs.current[0]?.focus(), 100);
        }
        // Wrong code → accrue toward lockout.
        try {
          const status = await recordFailure('totp');
          setRateLimit(status.allowed ? null : status);
        } catch (rlErr) {
          logger.error('recordFailure(totp) failed:', rlErr);
        }
      }
    } catch (error) {
      logger.error('TOTP verification error:', error);
      setErrorMessage('Failed to verify code. Please check your connection and try again.');
      // Deliberately no recordFailure on network errors — they're not
      // evidence of a wrong code.
    } finally {
      setIsVerifying(false);
    }
  };

  return (
    <View style={styles.container}>
      <StatusBar barStyle="light-content" backgroundColor="#29BFC0" />
      <KeyboardAvoidingView
        style={styles.keyboardAvoid}
        behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
      >
        <ScrollView
          contentContainerStyle={styles.scrollContent}
          keyboardShouldPersistTaps="handled"
          showsVerticalScrollIndicator={false}
        >
          {/* Header Section with Teal Background */}
          <View style={styles.headerSection}>
            <Image
              source={require('../../assets/logo.png')}
              style={styles.logo}
              resizeMode="contain"
            />
            <Text style={styles.title}>Two-Factor Authentication</Text>
            <Text style={styles.subtitle}>
              {useBackupCode
                ? 'Enter one of your backup codes'
                : 'One step closer. Please enter the code from your authenticator app:'}
            </Text>
          </View>

          {/* Error Banner */}
          {errorMessage && (
            <View style={styles.errorBanner}>
              <Text style={styles.errorTitle}>Authentication failed</Text>
              <Text style={styles.errorMessage}>{errorMessage}</Text>
            </View>
          )}

          {/* White Card Section */}
          <View style={styles.cardSection}>
            {/* Remember Me Toggle. Sits above the code on purpose: the sixth
                digit submits immediately, so a choice placed below the code
                could never be made in time. */}
            <View style={styles.rememberMeContainer}>
              <Switch
                value={rememberDevice}
                onValueChange={setRememberDevice}
                trackColor={{ false: '#E0E0E0', true: '#29BFC0' }}
                thumbColor={rememberDevice ? '#FFF' : '#FFF'}
                ios_backgroundColor="#E0E0E0"
              />
              <View style={styles.rememberMeTextContainer}>
                <Text style={styles.rememberMeText}>Remember this device for 30 days</Text>
                <Text style={styles.rememberMeSubtext}>
                  Skip two-factor authentication on this device
                </Text>
              </View>
            </View>

            {useBackupCode ? (
              <View style={styles.backupCodeContainer}>
                <Text style={styles.codeLabel}>Backup Code</Text>
                <TextInput
                  style={styles.backupCodeInput}
                  value={backupCode}
                  onChangeText={(text) => {
                    if (errorMessage) setErrorMessage(null);
                    setBackupCode(text.toUpperCase());
                  }}
                  placeholder="Enter 8-character code"
                  placeholderTextColor="#999"
                  autoCapitalize="characters"
                  autoCorrect={false}
                  maxLength={8}
                  editable={!isVerifying}
                  autoFocus
                />
              </View>
            ) : (
              <>
                <Text style={styles.codeLabel}>Verification Code</Text>
                <View style={styles.codeContainer}>
                  {code.map((digit, index) => (
                    <TextInput
                      key={index}
                      ref={(ref) => (inputRefs.current[index] = ref)}
                      style={styles.codeInput}
                      value={digit}
                      onChangeText={(text) => handleCodeChange(text, index)}
                      onKeyPress={(e) => handleKeyPress(e, index)}
                      keyboardType="number-pad"
                      maxLength={1}
                      autoFocus={index === 0}
                      editable={!isVerifying}
                    />
                  ))}
                </View>
              </>
            )}

            {rateLimit && !rateLimit.allowed ? (
              <RateLimitBanner
                remainingMs={rateLimit.remainingMs}
                count={rateLimit.count}
                onExpired={() => setRateLimit(null)}
              />
            ) : null}

            {/* Verify Button */}
            <TouchableOpacity
              style={[
                styles.verifyButton,
                (isVerifying || (rateLimit && !rateLimit.allowed)) &&
                  styles.verifyButtonDisabled,
              ]}
              onPress={() => handleVerify()}
              disabled={
                isVerifying ||
                !!(rateLimit && !rateLimit.allowed) ||
                (useBackupCode ? backupCode.length !== 8 : code.join('').length !== 6)
              }
            >
              {isVerifying ? (
                <ActivityIndicator color="#FFF" />
              ) : (
                <Text style={styles.verifyButtonText}>Verify</Text>
              )}
            </TouchableOpacity>

            {/* Use backup code link */}
            <TouchableOpacity
              style={styles.linkButton}
              onPress={() => {
                setUseBackupCode(!useBackupCode);
                setCode(['', '', '', '', '', '']);
                setBackupCode('');
                setErrorMessage(null);
              }}
              disabled={isVerifying}
            >
              <Text style={styles.linkButtonText}>
                {useBackupCode ? 'Use authenticator code' : 'Use backup code'}
              </Text>
            </TouchableOpacity>

            {/* Cancel link */}
            <TouchableOpacity
              style={styles.cancelButton}
              onPress={() => navigation.goBack()}
              disabled={isVerifying}
            >
              <Text style={styles.cancelButtonText}>Cancel</Text>
            </TouchableOpacity>
          </View>
        </ScrollView>
      </KeyboardAvoidingView>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#29BFC0',
  },
  keyboardAvoid: {
    flex: 1,
  },
  scrollContent: {
    flexGrow: 1,
  },
  headerSection: {
    backgroundColor: '#29BFC0',
    paddingTop: ms(80),
    paddingBottom: ms(40),
    paddingHorizontal: ms(24),
    alignItems: 'center',
    minHeight: height * 0.4,
    justifyContent: 'center',
  },
  logo: {
    width: s(100),
    height: s(100),
    marginBottom: ms(24),
  },
  title: {
    fontSize: ms(26, 0.3),
    fontWeight: 'bold',
    color: '#1C2C4F',
    marginBottom: ms(12),
    textAlign: 'center',
  },
  subtitle: {
    fontSize: ms(15, 0.3),
    color: '#1C2C4F',
    textAlign: 'center',
    lineHeight: ms(22, 0.3),
    paddingHorizontal: ms(20),
  },
  errorBanner: {
    backgroundColor: '#F8D7DA',
    marginHorizontal: ms(20),
    marginBottom: ms(-20),
    paddingVertical: ms(16),
    paddingHorizontal: ms(20),
    borderRadius: ms(12),
    zIndex: 1,
  },
  errorTitle: {
    fontSize: ms(16, 0.3),
    fontWeight: 'bold',
    color: '#721C24',
    marginBottom: ms(4),
    textAlign: 'center',
  },
  errorMessage: {
    fontSize: ms(14, 0.3),
    color: '#721C24',
    textAlign: 'center',
    lineHeight: ms(20, 0.3),
  },
  cardSection: {
    flex: 1,
    backgroundColor: '#FFFFFF',
    borderTopLeftRadius: ms(30),
    borderTopRightRadius: ms(30),
    paddingTop: ms(40),
    paddingHorizontal: ms(24),
    paddingBottom: ms(40),
    marginTop: ms(20),
    shadowColor: '#000',
    shadowOffset: { width: 0, height: -4 },
    shadowOpacity: 0.1,
    shadowRadius: 12,
    elevation: 10,
  },
  codeLabel: {
    fontSize: ms(16, 0.3),
    fontWeight: '600',
    color: '#29BFC0',
    marginBottom: ms(16),
  },
  codeContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: ms(32),
  },
  codeInput: {
    width: s(48),
    height: s(48),
    borderRadius: ms(12),
    fontSize: ms(20, 0.3),
    textAlign: 'center',
    backgroundColor: '#F5F5F5',
    color: '#1C2C4F',
    fontWeight: '600',
    borderWidth: 1,
    borderColor: '#E0E0E0',
  },
  backupCodeContainer: {
    marginBottom: ms(32),
  },
  backupCodeInput: {
    height: s(56),
    borderWidth: 1,
    borderColor: '#E0E0E0',
    borderRadius: ms(12),
    fontSize: ms(18, 0.3),
    textAlign: 'center',
    backgroundColor: '#F5F5F5',
    color: '#1C2C4F',
    fontFamily: 'monospace',
    letterSpacing: 2,
  },
  rememberMeContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#F8F9FA',
    padding: ms(16),
    borderRadius: ms(12),
    marginBottom: ms(24),
    borderWidth: 1,
    borderColor: '#E0E0E0',
  },
  rememberMeTextContainer: {
    flex: 1,
    marginLeft: ms(12),
  },
  rememberMeText: {
    fontSize: ms(15, 0.3),
    fontWeight: '600',
    color: '#1C2C4F',
  },
  rememberMeSubtext: {
    fontSize: ms(12, 0.3),
    color: '#666',
    marginTop: ms(2),
  },
  verifyButton: {
    backgroundColor: '#29BFC0',
    paddingVertical: ms(16),
    borderRadius: ms(30),
    alignItems: 'center',
    marginBottom: ms(20),
    shadowColor: '#29BFC0',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.3,
    shadowRadius: 8,
    elevation: 4,
  },
  verifyButtonDisabled: {
    opacity: 0.6,
  },
  verifyButtonText: {
    color: '#FFFFFF',
    fontSize: ms(18, 0.3),
    fontWeight: 'bold',
  },
  linkButton: {
    paddingVertical: ms(12),
    alignItems: 'center',
  },
  linkButtonText: {
    color: '#29BFC0',
    fontSize: ms(16, 0.3),
    fontWeight: '600',
  },
  cancelButton: {
    paddingVertical: ms(8),
    alignItems: 'center',
  },
  cancelButtonText: {
    color: '#666',
    fontSize: ms(14, 0.3),
  },
});

export default TotpVerificationScreen;
